import { useMemo } from "react";
import { useIntl } from "react-intl";
import { WANotePreviewTemplate } from "./WANotePreviewTemplate";
import { WACategoryNotePreviewsQuery$rawResponse } from "./__generated__/WACategoryNotePreviewsQuery.graphql";

function page({
  dataPrefix,
  index,
  backgroundColor = "white",
  currentValue = "",
}: {
  dataPrefix: string;
  index: number;
  backgroundColor?: string;
  currentValue?: string;
}) {
  return {
    currentValue,
    fontSize: "MEDIUM" as const,
    fontStyle: "DEFAULT" as const,
    fontColor: null,
    id: dataPrefix + "PAGE" + index,
    includedInSlideshow: false,
    backgroundColor,
    image: null,
    imageScale: 1,
    imageTranslateX: 0,
    imageTranslateY: 0,
    containImage: null,
    remoteImageURL: null,
    version: "1",
    updatedBy: null,
    updatedAtFormatted: null,
  };
}

const common = {
  editingMode: "READ_ONLY" as const,
  noteSize: "TWO_X_TWO" as const,
  isResizable: true,
  isOrgNote: false,
};

export function useMockPreviewWidgetNoteImageSlideshow(props: {
  dataPrefix: string;
}): NonNullable<
  WACategoryNotePreviewsQuery$rawResponse["dashboard"]
>["previewWidgetNote1"] {
  const intl = useIntl();

  const title =
    intl.formatMessage({
      defaultMessage: "Slideshow",
      description: "WANote Template title slideshow",
    }) ?? "";
  return useMemo(
    () => ({
      id: "w" + props.dataPrefix,
      title,
      note: {
        ...common,
        id: props.dataPrefix,
        title,
        isOrgNote: false,
        hub: null,
        pages: [
          {
            ...page({
              dataPrefix: props.dataPrefix,
              index: 1,
            }),
            fontSize: "LARGE" as const,
            WidgetNoteFontStyle: "BOLD" as const,
            currentValue:
              intl.formatMessage({
                defaultMessage: "New expo",
                description: "WANote Template Slideshow Content Page1 ",
              }) ?? "",
            image: {
              id: props.dataPrefix + "IMAGE",
              largeImage: {
                id: props.dataPrefix + "LARGE_IMAGE",
                height: 800,

                width: 533,
                url: "https://assets.storesprint.app/widget-note-template-images/exponering.png",
              },
            },
          },
          {
            ...page({
              dataPrefix: props.dataPrefix,
              index: 2,
            }),
            fontSize: "LARGE" as const,
            WidgetNoteFontStyle: "BOLD" as const,
            currentValue:
              intl.formatMessage({
                defaultMessage: "New fruits in store",
                description: "WANote Template Slideshow Content Page2 ",
              }) ?? "",
            image: {
              id: props.dataPrefix + "IMAGE 2",
              largeImage: {
                height: 800,

                width: 533,
                id: props.dataPrefix + "LARGE_IMAGE 2",
                url: "https://assets.storesprint.app/widget-note-template-images/slideshowFruit.png",
              },
            },
          },
          {
            ...page({
              dataPrefix: props.dataPrefix,
              index: 3,
            }),
            fontSize: "LARGE" as const,
            WidgetNoteFontStyle: "BOLD" as const,
            currentValue:
              intl.formatMessage({
                defaultMessage: "Welcome, Matthew, our new colleague!",
                description: "WANote Template Slideshow Content Page3 ",
              }) ?? "",
            image: {
              id: props.dataPrefix + "IMAGE 3",
              largeImage: {
                height: 800,

                width: 533,
                id: props.dataPrefix + "LARGE_IMAGE 3",
                url: "https://assets.storesprint.app/widget-note-template-images/slideshowMathias.png",
              },
            },
          },
        ],
      },
    }),
    [intl, props.dataPrefix, title]
  );
}

export function useMockPreviewWidgetNoteDailyPositive(props: {
  dataPrefix: string;
}): NonNullable<
  WACategoryNotePreviewsQuery$rawResponse["dashboard"]
>["previewWidgetNote1"] {
  const intl = useIntl();
  const title =
    intl.formatMessage({
      defaultMessage: "Good examples",
      description: "WANote Template title good examples",
    }) ?? "";
  return useMemo(
    () => ({
      id: "w" + props.dataPrefix,
      title,
      note: {
        ...common,
        id: props.dataPrefix,
        title,
        isOrgNote: false,
        hub: null,
        pages: [
          {
            ...page({
              dataPrefix: props.dataPrefix,
              index: 0,
              currentValue:
                intl.formatMessage({
                  defaultMessage: "Brilliant effort from Karin yesterday!! 👏",
                  description: "WANote Template note body good examples",
                }) ?? "",
            }),
            image: {
              id: props.dataPrefix + "IMAGE",
              largeImage: {
                id: props.dataPrefix + "LARGE_IMAGE",
                height: 1608,

                width: 1438,
                url: "https://assets.storesprint.app/widget-note-template-images/dailyPositive.jpg",
              },
            },
          },
        ],
      },
    }),
    [intl, props.dataPrefix, title]
  );
}

export function useMockPreviewWidgetNoteEmpty(props: {
  dataPrefix: string;
}): NonNullable<
  WACategoryNotePreviewsQuery$rawResponse["dashboard"]
>["previewWidgetNote1"] {
  return useMemo(
    () => ({
      id: "w" + props.dataPrefix,
      title: "",
      note: {
        ...common,
        id: props.dataPrefix,
        isOrgNote: false,
        hub: null,
        title: "",
        pages: [
          page({
            dataPrefix: props.dataPrefix,
            index: 0,
            backgroundColor: "white",
          }),
        ],
      },
    }),
    [props.dataPrefix]
  );
}

export function useWidgetNoteMockPreview(props: {
  template: WANotePreviewTemplate;
  dataPrefix: string;
}) {
  const dp = useMockPreviewWidgetNoteDailyPositive({
    dataPrefix: props.dataPrefix,
  });
  const empty = useMockPreviewWidgetNoteEmpty({
    dataPrefix: props.dataPrefix,
  });
  const slide = useMockPreviewWidgetNoteImageSlideshow({
    dataPrefix: props.dataPrefix,
  });

  return useMemo(() => {
    switch (props.template) {
      case "dailyPositive":
        return dp;
      case "empty":
        return empty;
      case "slideshow":
        return slide;
    }
  }, [dp, empty, props.template, slide]);
}
